import React from 'react';
import styles from './Input.module.scss';
import { Icon, IconName } from '../../../assets/icons';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Etiqueta del input */
  label: string;
  /** Mensaje de error */
  error?: string;
  /** Texto de ayuda */
  helpText?: string;
  /** Tamaño del input */
  size?: 'sm' | 'md' | 'lg';
  /** Icono a mostrar */
  icon?: IconName;
  /** Posición del icono */
  iconPosition?: 'left' | 'right';
  /** Input de ancho completo */
  fullWidth?: boolean;
  /** Input requerido */
  required?: boolean;
  /** Estado de carga */
  loading?: boolean;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  helpText,
  size = 'md',
  icon,
  iconPosition = 'left',
  fullWidth = false,
  required = false,
  loading = false,
  className,
  id,
  ...props
}, ref) => {
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
  
  const containerClasses = [
    styles.container,
    styles[`container--${size}`],
    fullWidth && styles['container--full-width'],
    error && styles['container--error'],
    loading && styles['container--loading'],
    className
  ].filter(Boolean).join(' ');

  const inputClasses = [
    styles.input,
    icon && styles[`input--with-icon-${iconPosition}`],
    error && styles['input--error']
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClasses}>
      <label htmlFor={inputId} className={styles.label}>
        {label}
        {required && <span className={styles.required}>*</span>}
      </label>
      
      <div className={styles.inputWrapper}>
        {icon && iconPosition === 'left' && (
          <Icon 
            name={icon} 
            className={styles.iconLeft}
            width={size === 'sm' ? 16 : size === 'lg' ? 20 : 18}
            height={size === 'sm' ? 16 : size === 'lg' ? 20 : 18}
          />
        )}
        
        <input
          ref={ref}
          id={inputId}
          className={inputClasses}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={
            [
              error && `${inputId}-error`,
              helpText && `${inputId}-help`
            ].filter(Boolean).join(' ') || undefined
          }
          disabled={loading}
          {...props}
        />
        
        {loading && (
          <div className={styles.spinner} />
        )}
        
        {icon && iconPosition === 'right' && (
          <Icon 
            name={icon} 
            className={styles.iconRight}
            width={size === 'sm' ? 16 : size === 'lg' ? 20 : 18}
            height={size === 'sm' ? 16 : size === 'lg' ? 20 : 18}
          />
        )}
      </div>
      
      {error && (
        <div id={`${inputId}-error`} className={styles.error} role="alert">
          {error}
        </div>
      )}
      
      {helpText && !error && (
        <div id={`${inputId}-help`} className={styles.helpText}>
          {helpText}
        </div>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input; 